<template>
  <div class="arco-brand-preview">
    <section class="hero">
      <div>
        <p class="hero__eyebrow">/ui-preview/arco-brand</p>
        <h1>Arco Brand UI 示例验收页</h1>
        <p class="hero__desc">
          该页面只用于浏览器视觉验收，所有数据均为本地写死，用来检查
          Element UI 组件是否已被全局品牌样式覆盖。
        </p>
      </div>
      <div class="hero__meta">
        <el-tag type="success" effect="dark">Brand #00A551</el-tag>
        <el-badge :value="12" class="hero__badge">
          <el-button size="small">待验收组件</el-button>
        </el-badge>
      </div>
    </section>

    <el-alert
      title="说明：页面自身仅提供轻量布局，主要观察全局主题、边框、阴影、悬停与状态色表现。"
      type="success"
      :closable="false"
      show-icon
      class="preview-alert"
    />

    <section class="preview-section">
      <div class="section-title">
        <h2>基础组件</h2>
        <span>Button / Link / Tag / Badge / Alert / Card / Empty / Result</span>
      </div>
      <el-row :gutter="16">
        <el-col :xs="24" :lg="12">
          <el-card shadow="hover" class="preview-card">
            <div slot="header">按钮与链接</div>
            <div class="inline-wrap">
              <el-button type="primary">主要按钮</el-button>
              <el-button type="success">成功按钮</el-button>
              <el-button type="warning">警告按钮</el-button>
              <el-button type="danger">危险按钮</el-button>
              <el-button type="primary" plain>朴素主按钮</el-button>
              <el-button type="warning" plain>朴素警告</el-button>
              <el-button type="danger" plain>朴素危险</el-button>
              <el-button plain>朴素按钮</el-button>
              <el-button round icon="el-icon-check">圆角按钮</el-button>
              <el-button type="primary" circle icon="el-icon-search" />
              <el-button type="primary" loading>加载中</el-button>
              <el-button disabled>禁用按钮</el-button>
              <el-button type="text">文字按钮</el-button>
            </div>
            <div class="inline-wrap">
              <el-button size="mini">Mini</el-button>
              <el-button size="small">Small</el-button>
              <el-button>Default</el-button>
              <el-button size="medium">Medium</el-button>
            </div>
            <div class="inline-wrap">
              <el-button-group>
                <el-button icon="el-icon-arrow-left">上一页</el-button>
                <el-button type="primary">
                  下一页
                  <i class="el-icon-arrow-right el-icon--right" />
                </el-button>
              </el-button-group>
            </div>
            <div class="inline-wrap">
              <el-link type="primary" :underline="false">主链接</el-link>
              <el-link type="success">成功链接</el-link>
              <el-link type="warning">警告链接</el-link>
              <el-link type="danger">危险链接</el-link>
            </div>
          </el-card>
        </el-col>
        <el-col :xs="24" :lg="12">
          <el-card shadow="hover" class="preview-card">
            <div slot="header">标签、角标与提示</div>
            <div class="inline-wrap">
              <el-tag>默认标签</el-tag>
              <el-tag closable>可关闭</el-tag>
              <el-tag effect="plain">朴素标签</el-tag>
              <el-tag type="success">成功</el-tag>
              <el-tag type="info">信息</el-tag>
              <el-tag type="warning">警告</el-tag>
              <el-tag type="danger">危险</el-tag>
              <el-tag type="danger" effect="dark">暗色危险</el-tag>
            </div>
            <div class="inline-wrap">
              <el-badge value="new">
                <el-button size="small">消息中心</el-button>
              </el-badge>
              <el-badge :value="8" type="primary">
                <el-button size="small">订单</el-button>
              </el-badge>
              <el-badge is-dot>
                <el-link type="primary">在线状态</el-link>
              </el-badge>
            </div>
            <el-alert
              title="基础组件区域可重点观察主色、文字层级、边框圆角与悬停态。"
              type="info"
              description="这条提示用于观察 description、close icon、边框和浅色状态。"
              show-icon
            />
          </el-card>
        </el-col>
      </el-row>

      <el-row :gutter="16">
        <el-col :xs="24" :lg="12">
          <el-card shadow="hover" class="preview-card">
            <div slot="header">空状态</div>
            <el-empty description="暂无待处理数据" :image-size="110" />
          </el-card>
        </el-col>
        <el-col :xs="24" :lg="12">
          <el-card shadow="hover" class="preview-card">
            <div slot="header">结果页</div>
            <el-result
              icon="success"
              title="品牌换肤校验通过"
              sub-title="继续检查表单、数据和反馈组件的交互状态。"
            >
              <template slot="extra">
                <el-button type="primary" size="small">继续验收</el-button>
                <el-button size="small">查看规范</el-button>
              </template>
            </el-result>
          </el-card>
        </el-col>
      </el-row>
    </section>

    <section class="preview-section">
      <div class="section-title">
        <h2>媒体与服务式反馈</h2>
        <span>Avatar / Image / ImageViewer / Divider / Message / MessageBox / Notification / Loading</span>
      </div>
      <el-row :gutter="16">
        <el-col :xs="24" :lg="12">
          <el-card shadow="hover" class="preview-card">
            <div slot="header">头像、图片与分割线</div>
            <div class="inline-wrap media-row">
              <el-avatar :size="44" icon="el-icon-user-solid" />
              <el-avatar :size="44" shape="square">AJ</el-avatar>
              <el-image
                class="preview-image"
                :src="previewImage"
                :preview-src-list="previewImages"
                fit="cover"
              />
              <el-image
                class="preview-image"
                :src="placeholderImage"
                fit="cover"
              />
              <el-button size="small" @click="imageViewerVisible = true">打开 ImageViewer</el-button>
            </div>
            <el-divider content-position="left">横向分割线</el-divider>
            <div class="divider-demo">
              <span>包裹</span>
              <el-divider direction="vertical" />
              <span>路由</span>
              <el-divider direction="vertical" />
              <span>轨迹</span>
            </div>
          </el-card>
        </el-col>
        <el-col :xs="24" :lg="12">
          <el-card shadow="hover" class="preview-card">
            <div slot="header">服务式反馈</div>
            <div class="inline-wrap">
              <el-button type="primary" @click="showMessage('success')">成功 Message</el-button>
              <el-button @click="showMessage('info')">信息 Message</el-button>
              <el-button type="warning" @click="showMessage('warning')">警告 Message</el-button>
              <el-button type="danger" @click="showMessage('error')">错误 Message</el-button>
              <el-button @click="showNotification('success')">成功 Notification</el-button>
              <el-button @click="showNotification('warning')">警告 Notification</el-button>
              <el-button @click="showMessageBox">MessageBox</el-button>
              <el-button @click="showLoading">Loading</el-button>
            </div>
            <p class="muted-text">
              这些组件通常挂载到 body，必须单独检查阴影、圆角、按钮主色和遮罩层。
            </p>
          </el-card>
        </el-col>
      </el-row>
    </section>

    <section class="preview-section">
      <div class="section-title">
        <h2>表单组件</h2>
        <span>Form / Input / Select / Cascader / Checkbox / Radio / Switch / Picker / Upload</span>
      </div>
      <el-card shadow="hover" class="preview-card">
        <el-form
          ref="previewForm"
          :model="form"
          label-width="110px"
          label-position="right"
          class="preview-form"
        >
          <el-row :gutter="16">
            <el-col :xs="24" :md="12" :xl="8">
              <el-form-item label="文本输入" required>
                <el-input
                  v-model="form.name"
                  placeholder="请输入品牌名称"
                  clearable
                  prefix-icon="el-icon-edit"
                  suffix-icon="el-icon-circle-check"
                />
              </el-form-item>
            </el-col>
            <el-col :xs="24" :md="12" :xl="8">
              <el-form-item label="组合输入">
                <el-input
                  v-model="form.orderNo"
                  placeholder="请输入订单号"
                  clearable
                >
                  <template slot="prepend">AJ</template>
                  <template slot="append">查询</template>
                </el-input>
              </el-form-item>
            </el-col>
            <el-col :xs="24" :md="12" :xl="8">
              <el-form-item label="禁用输入">
                <el-input
                  v-model="form.disabledText"
                  disabled
                  placeholder="禁用态"
                />
              </el-form-item>
            </el-col>
            <el-col :xs="24" :md="12" :xl="8">
              <el-form-item label="错误状态" class="is-error">
                <el-input
                  v-model="form.errorText"
                  placeholder="错误状态输入框"
                />
                <div class="el-form-item__error">请填写正确的品牌名称</div>
              </el-form-item>
            </el-col>
            <el-col :xs="24" :md="12" :xl="8">
              <el-form-item label="成功状态" class="is-success">
                <el-input
                  v-model="form.successText"
                  placeholder="成功状态输入框"
                />
              </el-form-item>
            </el-col>
            <el-col :xs="24" :md="12" :xl="8">
              <el-form-item label="多行文本">
                <el-input
                  v-model="form.remark"
                  type="textarea"
                  maxlength="80"
                  show-word-limit
                  :rows="3"
                  placeholder="观察 textarea 背景、字数统计和 focus ring"
                />
              </el-form-item>
            </el-col>
            <el-col :xs="24" :md="12" :xl="8">
              <el-form-item label="数字输入">
                <el-input-number
                  v-model="form.amount"
                  :min="0"
                  :max="999"
                  controls-position="right"
                  style="width: 100%;"
                />
              </el-form-item>
            </el-col>
            <el-col :xs="24" :md="12" :xl="8">
              <el-form-item label="基础下拉">
                <el-select
                  v-model="form.status"
                  placeholder="请选择状态"
                  clearable
                  filterable
                  style="width: 100%;"
                >
                  <el-option-group label="常用状态">
                    <el-option label="启用" value="enabled" />
                    <el-option label="草稿" value="draft" />
                  </el-option-group>
                  <el-option-group label="不可用状态">
                    <el-option label="停用" value="disabled" disabled />
                  </el-option-group>
                </el-select>
              </el-form-item>
            </el-col>
            <el-col :xs="24" :md="12" :xl="8">
              <el-form-item label="多选下拉">
                <el-select
                  v-model="form.multiStatus"
                  multiple
                  collapse-tags
                  filterable
                  placeholder="请选择状态"
                  style="width: 100%;"
                >
                  <el-option
                    v-for="item in statusOptions"
                    :key="item.value"
                    :label="item.label"
                    :value="item.value"
                  />
                </el-select>
              </el-form-item>
            </el-col>
            <el-col :xs="24" :md="12" :xl="8">
              <el-form-item label="加载下拉">
                <el-select
                  v-model="form.loadingStatus"
                  loading
                  placeholder="加载中"
                  style="width: 100%;"
                />
              </el-form-item>
            </el-col>
            <el-col :xs="24" :md="12" :xl="8">
              <el-form-item label="禁用下拉">
                <el-select
                  v-model="form.disabledStatus"
                  disabled
                  placeholder="禁用状态"
                  style="width: 100%;"
                >
                  <el-option label="禁用已选" value="disabled-selected" />
                </el-select>
              </el-form-item>
            </el-col>
            <el-col :xs="24" :md="12" :xl="8">
              <el-form-item label="级联选择">
                <el-cascader
                  v-model="form.region"
                  :options="regionOptions"
                  clearable
                  style="width: 100%;"
                />
              </el-form-item>
            </el-col>
            <el-col :xs="24" :md="12" :xl="8">
              <el-form-item label="级联多选">
                <el-cascader
                  v-model="form.multiRegion"
                  :options="regionOptions"
                  :props="{ multiple: true }"
                  collapse-tags
                  clearable
                  style="width: 100%;"
                />
              </el-form-item>
            </el-col>
            <el-col :xs="24" :md="12" :xl="8">
              <el-form-item label="禁用级联">
                <el-cascader
                  v-model="form.disabledRegion"
                  :options="regionOptions"
                  disabled
                  style="width: 100%;"
                />
              </el-form-item>
            </el-col>
            <el-col :xs="24" :md="12" :xl="8">
              <el-form-item label="多选框">
                <el-checkbox-group v-model="form.features">
                  <el-checkbox label="hover">悬停态</el-checkbox>
                  <el-checkbox label="focus">聚焦态</el-checkbox>
                  <el-checkbox label="disabled" disabled>禁用态</el-checkbox>
                </el-checkbox-group>
              </el-form-item>
            </el-col>
            <el-col :xs="24" :md="12" :xl="8">
              <el-form-item label="半选与禁用">
                <div class="inline-wrap inline-wrap--compact">
                  <el-checkbox
                    v-model="form.indeterminateChecked"
                    :indeterminate="true"
                  >
                    半选
                  </el-checkbox>
                  <el-checkbox v-model="form.disabledChecked" disabled>
                    禁用已选
                  </el-checkbox>
                </div>
              </el-form-item>
            </el-col>
            <el-col :xs="24" :md="12" :xl="8">
              <el-form-item label="按钮多选">
                <el-checkbox-group v-model="form.buttonFeatures">
                  <el-checkbox-button label="table">表格</el-checkbox-button>
                  <el-checkbox-button label="form">表单</el-checkbox-button>
                  <el-checkbox-button label="overlay">浮层</el-checkbox-button>
                </el-checkbox-group>
              </el-form-item>
            </el-col>
            <el-col :xs="24" :md="12" :xl="8">
              <el-form-item label="单选框">
                <el-radio-group v-model="form.scene">
                  <el-radio label="light">浅色页</el-radio>
                  <el-radio label="dashboard">仪表盘</el-radio>
                  <el-radio label="disabled" disabled>禁用项</el-radio>
                </el-radio-group>
              </el-form-item>
            </el-col>
            <el-col :xs="24" :md="12" :xl="8">
              <el-form-item label="按钮单选">
                <el-radio-group v-model="form.mode">
                  <el-radio-button label="formal">正式</el-radio-button>
                  <el-radio-button label="test">测试</el-radio-button>
                  <el-radio-button label="disabled" disabled>禁用</el-radio-button>
                </el-radio-group>
              </el-form-item>
            </el-col>
            <el-col :xs="24" :md="12" :xl="8">
              <el-form-item label="开关">
                <el-switch
                  v-model="form.enabled"
                  active-text="已启用"
                  inactive-text="已停用"
                />
              </el-form-item>
            </el-col>
            <el-col :xs="24" :md="12" :xl="8">
              <el-form-item label="禁用开关">
                <el-switch
                  v-model="form.disabledSwitch"
                  disabled
                  active-text="禁用已启用"
                />
              </el-form-item>
            </el-col>
            <el-col :xs="24" :md="12" :xl="8">
              <el-form-item label="关闭/禁用开关">
                <div class="inline-wrap inline-wrap--compact">
                  <el-switch
                    v-model="form.enabledOff"
                    active-text="开"
                    inactive-text="关"
                  />
                  <el-switch
                    v-model="form.disabledSwitchOff"
                    disabled
                    inactive-text="禁用关闭"
                  />
                </div>
              </el-form-item>
            </el-col>
            <el-col :xs="24" :md="12" :xl="8">
              <el-form-item label="日期选择">
                <el-date-picker
                  v-model="form.date"
                  type="date"
                  placeholder="选择日期"
                  :picker-options="datePickerOptions"
                  style="width: 100%;"
                />
              </el-form-item>
            </el-col>
            <el-col :xs="24" :md="12" :xl="8">
              <el-form-item label="日期范围">
                <el-date-picker
                  v-model="form.dateRange"
                  type="daterange"
                  range-separator="至"
                  start-placeholder="开始日期"
                  end-placeholder="结束日期"
                  :picker-options="dateRangePickerOptions"
                  style="width: 100%;"
                />
              </el-form-item>
            </el-col>
            <el-col :xs="24" :md="12" :xl="8">
              <el-form-item label="多日期">
                <el-date-picker
                  v-model="form.dates"
                  type="dates"
                  placeholder="选择多个日期"
                  style="width: 100%;"
                />
              </el-form-item>
            </el-col>
            <el-col :xs="24" :md="12" :xl="8">
              <el-form-item label="月份范围">
                <el-date-picker
                  v-model="form.monthRange"
                  type="monthrange"
                  range-separator="至"
                  start-placeholder="开始月份"
                  end-placeholder="结束月份"
                  style="width: 100%;"
                />
              </el-form-item>
            </el-col>
            <el-col :xs="24" :md="12" :xl="8">
              <el-form-item label="禁用日期">
                <el-date-picker
                  v-model="form.disabledDate"
                  type="date"
                  disabled
                  placeholder="禁用日期"
                  style="width: 100%;"
                />
              </el-form-item>
            </el-col>
            <el-col :xs="24" :md="12" :xl="8">
              <el-form-item label="时间选择">
                <el-time-picker
                  v-model="form.time"
                  placeholder="选择时间"
                  style="width: 100%;"
                />
              </el-form-item>
            </el-col>
            <el-col :xs="24" :md="12" :xl="8">
              <el-form-item label="时间范围">
                <el-time-picker
                  v-model="form.timeRange"
                  is-range
                  range-separator="至"
                  start-placeholder="开始时间"
                  end-placeholder="结束时间"
                  style="width: 100%;"
                />
              </el-form-item>
            </el-col>
            <el-col :xs="24" :md="12" :xl="8">
              <el-form-item label="禁用时间">
                <el-time-picker
                  v-model="form.disabledTime"
                  disabled
                  placeholder="禁用时间"
                  style="width: 100%;"
                />
              </el-form-item>
            </el-col>
            <el-col :xs="24" :md="12" :xl="8">
              <el-form-item label="el-select-v2">
                <el-select-v2
                  v-model="form.selectV2"
                  :options="selectV2Options"
                  filterable
                  clearable
                  placeholder="请选择站点"
                  style="width: 100%;"
                />
              </el-form-item>
            </el-col>
            <el-col :xs="24" :md="12" :xl="8">
              <el-form-item label="el-select-v2 多选">
                <el-select-v2
                  v-model="form.selectV2Multiple"
                  :options="selectV2Options"
                  multiple
                  collapse-tags
                  filterable
                  placeholder="请选择多个站点"
                  style="width: 100%;"
                />
              </el-form-item>
            </el-col>
            <el-col :xs="24" :md="12" :xl="8">
              <el-form-item label="el-select-v2 禁用">
                <el-select-v2
                  v-model="form.disabledSelectV2"
                  :options="selectV2Options"
                  disabled
                  placeholder="禁用站点"
                  style="width: 100%;"
                />
              </el-form-item>
            </el-col>
            <el-col :xs="24" :xl="16">
              <el-form-item label="上传">
                <el-upload
                  action="#"
                  :auto-upload="false"
                  :file-list="uploadFiles"
                  list-type="text"
                  :on-change="handleUploadChange"
                  :on-remove="handleUploadRemove"
                >
                  <el-button size="small" type="primary">选择文件</el-button>
                  <div slot="tip" class="el-upload__tip">
                    仅演示上传列表、按钮、提示文案和边框样式，不会发起真实请求。
                  </div>
                </el-upload>
              </el-form-item>
            </el-col>
            <el-col :xs="24" :xl="8">
              <el-form-item label="拖拽上传">
                <el-upload
                  action="#"
                  drag
                  :auto-upload="false"
                  :file-list="[]"
                >
                  <i class="el-icon-upload" />
                  <div class="el-upload__text">拖拽文件到这里，或<em>点击上传</em></div>
                </el-upload>
              </el-form-item>
            </el-col>
          </el-row>
        </el-form>
      </el-card>
    </section>

    <section class="preview-section">
      <div class="section-title">
        <h2>数据展示</h2>
        <span>Table / Pagination / Descriptions / Tree / Progress / Timeline</span>
      </div>
      <el-row :gutter="16">
        <el-col :xs="24" :xl="16">
          <el-card shadow="hover" class="preview-card">
            <div slot="header">表格与分页</div>
            <el-table
              :data="tableData"
              border
              stripe
              show-summary
              highlight-current-row
              current-row-key="el-form"
              row-key="name"
              style="width: 100%;"
            >
              <el-table-column type="selection" width="48" fixed />
              <el-table-column type="expand" width="48">
                <template slot-scope="{ row }">
                  <div class="table-expand">
                    {{ row.name }} 对标 {{ row.arcoPath }}，重点观察展开区域背景与固定列阴影。
                  </div>
                </template>
              </el-table-column>
              <el-table-column
                prop="name"
                label="组件"
                min-width="140"
                fixed
                sortable
              />
              <el-table-column
                prop="category"
                label="分类"
                min-width="120"
                :filters="categoryFilters"
                :filter-method="filterCategory"
              />
              <el-table-column
                prop="count"
                label="状态数"
                min-width="100"
                sortable
              />
              <el-table-column prop="status" label="状态" min-width="120">
                <template slot-scope="{ row }">
                  <el-tag :type="row.statusType" size="small">{{ row.status }}</el-tag>
                </template>
              </el-table-column>
              <el-table-column prop="desc" label="说明" min-width="220" />
              <el-table-column label="操作" width="140" fixed="right">
                <template slot-scope="{ row }">
                  <el-button type="text">查看</el-button>
                  <el-button type="text">记录 {{ row.count }}</el-button>
                </template>
              </el-table-column>
            </el-table>
            <div class="pagination-wrap">
              <el-pagination
                background
                layout="total, sizes, prev, pager, next, jumper"
                :current-page="1"
                :page-size="5"
                :page-sizes="[5, 10, 20]"
                :total="15"
              />
            </div>
            <div class="pagination-wrap pagination-wrap--compact">
              <el-pagination
                small
                layout="prev, pager, next"
                :current-page="1"
                :page-size="5"
                :total="15"
              />
              <el-pagination
                disabled
                layout="prev, pager, next"
                :current-page="1"
                :page-size="5"
                :total="15"
              />
            </div>
          </el-card>
        </el-col>
        <el-col :xs="24" :xl="8">
          <el-card shadow="hover" class="preview-card">
            <div slot="header">描述列表</div>
            <el-descriptions :column="1" border>
              <el-descriptions-item label="页面用途">视觉验收</el-descriptions-item>
              <el-descriptions-item label="数据来源">本地静态数据</el-descriptions-item>
              <el-descriptions-item label="路由策略">constantRoutes hidden</el-descriptions-item>
              <el-descriptions-item label="品牌主色">#00A551</el-descriptions-item>
            </el-descriptions>
          </el-card>
        </el-col>
      </el-row>

      <el-row :gutter="16">
        <el-col :xs="24" :lg="8">
          <el-card shadow="hover" class="preview-card">
            <div slot="header">树形结构</div>
            <el-tree
              :data="treeData"
              node-key="id"
              default-expand-all
              show-checkbox
              highlight-current
              draggable
              current-node-key="12"
              :default-checked-keys="[13]"
              :expand-on-click-node="false"
            />
          </el-card>
        </el-col>
        <el-col :xs="24" :lg="8">
          <el-card shadow="hover" class="preview-card">
            <div slot="header">进度条</div>
            <div class="stack-wrap">
              <el-progress :percentage="28" />
              <el-progress :percentage="62" status="success" />
              <el-progress :percentage="76" status="warning" />
              <el-progress :percentage="90" status="exception" />
              <el-progress type="circle" :percentage="68" />
            </div>
          </el-card>
        </el-col>
        <el-col :xs="24" :lg="8">
          <el-card shadow="hover" class="preview-card">
            <div slot="header">时间线</div>
            <el-timeline>
              <el-timeline-item
                v-for="item in timelineData"
                :key="item.title"
                :timestamp="item.time"
                :type="item.type"
              >
                {{ item.title }}
              </el-timeline-item>
            </el-timeline>
          </el-card>
        </el-col>
      </el-row>
    </section>

    <section class="preview-section">
      <div class="section-title">
        <h2>反馈组件</h2>
        <span>Dialog / Drawer / Dropdown / Popover / Popconfirm / Tooltip</span>
      </div>
      <el-card shadow="hover" class="preview-card">
        <div class="inline-wrap">
          <el-button type="primary" @click="dialogVisible = true">打开 Dialog</el-button>
          <el-button @click="drawerVisible = true">打开 Drawer</el-button>
          <el-dropdown split-button type="primary">
            操作菜单
            <el-dropdown-menu slot="dropdown">
              <el-dropdown-item>查看详情</el-dropdown-item>
              <el-dropdown-item>复制链接</el-dropdown-item>
              <el-dropdown-item divided>归档</el-dropdown-item>
            </el-dropdown-menu>
          </el-dropdown>
          <el-popover
            placement="top"
            width="220"
            trigger="hover"
            content="这里可以观察气泡背景、阴影、箭头和文本样式。"
          >
            <el-button slot="reference">悬停 Popover</el-button>
          </el-popover>
          <el-popconfirm
            title="确认仅作演示，不执行真实删除吗？"
            confirm-button-text="确认"
            cancel-button-text="取消"
          >
            <el-button slot="reference" type="danger" plain>Popconfirm</el-button>
          </el-popconfirm>
          <el-tooltip content="Tooltip 文案示例" placement="top">
            <el-button>Tooltip</el-button>
          </el-tooltip>
        </div>
      </el-card>
    </section>

    <section class="preview-section">
      <div class="section-title">
        <h2>导航组件</h2>
        <span>Tabs / Breadcrumb / Collapse / Steps / Carousel / Scrollbar</span>
      </div>
      <el-row :gutter="16">
        <el-col :xs="24" :xl="14">
          <el-card shadow="hover" class="preview-card">
            <div slot="header">页签与面包屑</div>
            <el-breadcrumb separator="/">
              <el-breadcrumb-item>UI Preview</el-breadcrumb-item>
              <el-breadcrumb-item>Arco Brand</el-breadcrumb-item>
              <el-breadcrumb-item>Visual Check</el-breadcrumb-item>
            </el-breadcrumb>
            <el-tabs v-model="activeTab" class="preview-tabs">
              <el-tab-pane label="组件状态" name="state">
                <p>观察选中态、标签间距、下划线或卡片式样式。</p>
              </el-tab-pane>
              <el-tab-pane label="交互反馈" name="feedback">
                <p>观察 hover、active、disabled 等状态色是否统一。</p>
              </el-tab-pane>
              <el-tab-pane label="布局间距" name="layout">
                <p>观察卡片、区块、标题、内边距是否符合品牌基调。</p>
              </el-tab-pane>
              <el-tab-pane label="禁用项" name="disabled" disabled>
                <p>禁用项仅用于观察颜色。</p>
              </el-tab-pane>
            </el-tabs>
            <el-tabs v-model="activeCardTab" type="card" class="preview-tabs">
              <el-tab-pane label="卡片 Tab" name="card-a">卡片式页签。</el-tab-pane>
              <el-tab-pane label="禁用 Card" name="card-b" disabled>禁用卡片。</el-tab-pane>
            </el-tabs>
            <el-tabs v-model="activeBorderTab" type="border-card" class="preview-tabs">
              <el-tab-pane label="Border A" name="border-a">带边框页签。</el-tab-pane>
              <el-tab-pane label="Border B" name="border-b">观察 header 背景。</el-tab-pane>
            </el-tabs>
          </el-card>
        </el-col>
        <el-col :xs="24" :xl="10">
          <el-card shadow="hover" class="preview-card">
            <div slot="header">步骤条</div>
            <el-steps :active="2" finish-status="success" align-center>
              <el-step title="加载样式" />
              <el-step title="逐项验收" />
              <el-step title="记录问题" />
            </el-steps>
            <el-steps :active="1" simple class="preview-steps-simple">
              <el-step title="简单步骤" />
              <el-step title="进行中" />
              <el-step title="等待" />
            </el-steps>
            <el-steps :active="1" direction="vertical" class="preview-steps-vertical">
              <el-step title="垂直成功" status="success" />
              <el-step title="垂直错误" status="error" />
              <el-step title="垂直等待" />
            </el-steps>
          </el-card>
        </el-col>
      </el-row>

      <el-row :gutter="16">
        <el-col :xs="24" :lg="12">
          <el-card shadow="hover" class="preview-card">
            <div slot="header">折叠面板</div>
            <el-collapse v-model="activeCollapse">
              <el-collapse-item name="basic" title="基础组件观察点">
                注意按钮主色、普通态背景、文字对比度与 tag 边框。
              </el-collapse-item>
              <el-collapse-item name="form" title="表单组件观察点">
                注意 input 聚焦边框、select 下拉面板、checkbox/radio 选中态。
              </el-collapse-item>
              <el-collapse-item name="data" title="数据组件观察点">
                注意表头、分隔线、分页、timeline 节点颜色。
              </el-collapse-item>
              <el-collapse-item name="disabled" title="禁用折叠项" disabled>
                仅用于观察禁用态。
              </el-collapse-item>
            </el-collapse>
          </el-card>
        </el-col>
        <el-col :xs="24" :lg="12">
          <el-card shadow="hover" class="preview-card">
            <div slot="header">轮播图</div>
            <el-carousel height="180px" indicator-position="outside">
              <el-carousel-item
                v-for="item in carouselItems"
                :key="item.title"
              >
                <div class="carousel-slide">
                  <strong>{{ item.title }}</strong>
                  <span>{{ item.desc }}</span>
                </div>
              </el-carousel-item>
            </el-carousel>
          </el-card>
        </el-col>
      </el-row>

      <el-card shadow="hover" class="preview-card">
        <div slot="header">滚动条</div>
        <el-scrollbar style="height: 180px;">
          <div class="scroll-demo">
            <div v-for="item in scrollbarItems" :key="item" class="scroll-item">
              {{ item }}
            </div>
          </div>
        </el-scrollbar>
        <el-scrollbar class="horizontal-scrollbar">
          <div class="horizontal-scrollbar__content">
            <span v-for="item in horizontalItems" :key="item">{{ item }}</span>
          </div>
        </el-scrollbar>
      </el-card>

      <el-row :gutter="16">
        <el-col :xs="24" :xl="12">
          <el-card shadow="hover" class="preview-card">
            <div slot="header">菜单状态</div>
            <el-menu
              :default-active="previewMenuActive"
              class="preview-menu"
              mode="vertical"
              @select="previewMenuActive = $event"
            >
              <el-menu-item index="dashboard">
                <i class="el-icon-data-line" />
                <span slot="title">数据看板</span>
              </el-menu-item>
              <el-submenu index="order">
                <template slot="title">
                  <i class="el-icon-truck" />
                  <span>订单中心</span>
                </template>
                <el-menu-item index="order-list">订单列表</el-menu-item>
                <el-menu-item index="order-track">轨迹查询</el-menu-item>
                <el-submenu index="order-more">
                  <template slot="title">更多操作</template>
                  <el-menu-item index="order-more-import">批量导入</el-menu-item>
                  <el-menu-item index="order-more-export">批量导出</el-menu-item>
                </el-submenu>
              </el-submenu>
              <el-menu-item index="settings" disabled>
                <i class="el-icon-setting" />
                <span slot="title">禁用菜单</span>
              </el-menu-item>
            </el-menu>
          </el-card>
        </el-col>
        <el-col :xs="24" :xl="12">
          <el-card shadow="hover" class="preview-card">
            <div slot="header">容器布局</div>
            <el-container class="container-demo">
              <el-aside width="140px">Aside</el-aside>
              <el-container>
                <el-main>
                  <i class="el-icon-box container-demo__icon" />
                  Main 内容区
                </el-main>
              </el-container>
            </el-container>
          </el-card>
        </el-col>
      </el-row>
    </section>

    <section class="preview-section">
      <div class="section-title">
        <h2>后台壳层</h2>
        <span>Sidebar / Navbar / TagsView / AppMain static shell</span>
      </div>
      <el-card shadow="hover" class="preview-card shell-card">
        <div class="shell-demo">
          <aside class="shell-sidebar">
            <div class="shell-logo">
              <span class="shell-logo__mark">AJ</span>
              <span>安骏快递</span>
            </div>
            <div
              v-for="item in shellMenu"
              :key="item.label"
              :class="['shell-menu-item', { 'is-active': item.active }]"
            >
              <i :class="item.icon" />
              <span>{{ item.label }}</span>
            </div>
          </aside>
          <main class="shell-main">
            <header class="shell-navbar">
              <div class="shell-breadcrumb">
                <i class="el-icon-s-fold" />
                <span>首页 / UI Preview / Arco Brand</span>
              </div>
              <div class="shell-actions">
                <span><i class="el-icon-bell" /></span>
                <span><i class="el-icon-search" /></span>
                <div class="shell-user">
                  <el-avatar :size="30">AJ</el-avatar>
                  <div>
                    <strong>运营中心</strong>
                    <em>Admin User</em>
                  </div>
                  <i class="el-icon-arrow-down" />
                </div>
              </div>
            </header>
            <div class="shell-tags">
              <span class="shell-tag is-active">工作台</span>
              <span class="shell-tag">订单管理 <i class="el-icon-close" /></span>
              <span class="shell-tag">轨迹查询 <i class="el-icon-close" /></span>
            </div>
            <div class="shell-content">
              <div class="shell-stat">
                <span>今日订单</span>
                <strong>12,840</strong>
              </div>
              <div class="shell-stat">
                <span>妥投率</span>
                <strong>98.6%</strong>
              </div>
              <div class="shell-stat">
                <span>异常件</span>
                <strong>32</strong>
              </div>
            </div>
          </main>
        </div>
      </el-card>
    </section>

    <section class="preview-section">
      <div class="section-title">
        <h2>特殊边界</h2>
        <span>el-select-v2 已实装；virtual-list / apexchart / vue-esign 使用占位卡片</span>
      </div>
      <el-row :gutter="16">
        <el-col :xs="24" :md="8">
          <el-card shadow="hover" class="preview-card placeholder-card">
            <div slot="header">virtual-list 占位</div>
            <p>仓库内存在 `vue-virtual-scroll-list` 依赖。</p>
            <p>本验收页不强依赖其运行，仅保留卡片用于观察容器、标题和说明文字样式。</p>
          </el-card>
        </el-col>
        <el-col :xs="24" :md="8">
          <el-card shadow="hover" class="preview-card placeholder-card">
            <div slot="header">apexchart 占位</div>
            <p>项目已全局注册 `vue-apexcharts`。</p>
            <p>为避免引入额外图表配置噪音，这里仅保留占位说明，不做真实图表渲染。</p>
          </el-card>
        </el-col>
        <el-col :xs="24" :md="8">
          <el-card shadow="hover" class="preview-card placeholder-card">
            <div slot="header">vue-esign 占位</div>
            <p>签名板用于观察边框和操作区时通常需要更多交互。</p>
            <p>本页仅保留静态占位，避免误触和无意义的画布依赖。</p>
          </el-card>
        </el-col>
      </el-row>
    </section>

    <el-dialog
      title="Dialog 示例"
      :visible.sync="dialogVisible"
      width="460px"
    >
      <p>这里可以检查弹窗的圆角、阴影、头部、正文排版和按钮主色。</p>
      <span slot="footer">
        <el-button @click="dialogVisible = false">取 消</el-button>
        <el-button type="primary" @click="dialogVisible = false">确 定</el-button>
      </span>
    </el-dialog>

    <el-drawer
      title="Drawer 示例"
      :visible.sync="drawerVisible"
      :with-header="true"
      size="32%"
    >
      <div class="drawer-content">
        <p>抽屉适合观察遮罩、边缘阴影、标题样式和内部滚动区域。</p>
        <el-descriptions :column="1" border>
          <el-descriptions-item label="类型">视觉验收辅助</el-descriptions-item>
          <el-descriptions-item label="交互">仅本地状态</el-descriptions-item>
          <el-descriptions-item label="风险">无接口依赖</el-descriptions-item>
        </el-descriptions>
      </div>
    </el-drawer>

    <el-image-viewer
      v-if="imageViewerVisible"
      :on-close="() => { imageViewerVisible = false; }"
      :url-list="previewImages"
    />
  </div>
</template>

<script>
import ElSelectV2 from 'el-select-v2';
import ElImageViewer from 'element-ui/packages/image/src/image-viewer';

export default {
  name: 'UiPreviewArcoBrand',
  components: {
    ElSelectV2,
    ElImageViewer
  },
  data() {
    return {
      dialogVisible: false,
      drawerVisible: false,
      imageViewerVisible: false,
      activeTab: 'state',
      activeCardTab: 'card-a',
      activeBorderTab: 'border-a',
      activeCollapse: ['basic'],
      previewMenuActive: 'dashboard',
      previewImage: require('@/assets/images/carousel/1.jpg'),
      placeholderImage: 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="160" height="100"><rect width="160" height="100" fill="%23f2fbf6"/><text x="80" y="54" font-size="14" text-anchor="middle" fill="%2300a551">Image</text></svg>',
      previewImages: [
        require('@/assets/images/carousel/1.jpg'),
        require('@/assets/images/carousel/2.jpg')
      ],
      uploadFiles: [
        {
          name: 'brand-checklist.pdf',
          url: '#'
        }
      ],
      form: {
        name: 'Arco Brand',
        orderNo: 'AJ20260602001',
        disabledText: '系统生成编号',
        errorText: 'Arco',
        successText: '品牌状态正常',
        remark: '用一组完整状态观察输入框、文本域、组合输入和校验反馈。',
        amount: 128,
        status: 'enabled',
        multiStatus: ['enabled', 'draft'],
        loadingStatus: '',
        disabledStatus: 'disabled-selected',
        region: ['brazil', 'sp', 'campinas'],
        multiRegion: [['brazil', 'sp', 'campinas']],
        disabledRegion: ['brazil', 'rj', 'rio-city'],
        features: ['hover', 'focus'],
        indeterminateChecked: true,
        disabledChecked: true,
        buttonFeatures: ['table', 'form'],
        scene: 'dashboard',
        mode: 'formal',
        enabled: true,
        enabledOff: false,
        disabledSwitch: true,
        disabledSwitchOff: false,
        date: new Date('2026-06-02T00:00:00'),
        dateRange: [
          new Date('2026-06-02T00:00:00'),
          new Date('2026-06-08T00:00:00')
        ],
        dates: [
          new Date('2026-06-03T00:00:00'),
          new Date('2026-06-06T00:00:00')
        ],
        monthRange: [
          new Date('2026-06-01T00:00:00'),
          new Date('2026-08-01T00:00:00')
        ],
        disabledDate: new Date('2026-06-02T00:00:00'),
        time: new Date('2026-06-02T09:30:00'),
        timeRange: [
          new Date('2026-06-02T09:30:00'),
          new Date('2026-06-02T18:30:00')
        ],
        disabledTime: new Date('2026-06-02T13:30:00'),
        selectV2: 'site-02',
        selectV2Multiple: ['site-01', 'site-02'],
        disabledSelectV2: 'site-03'
      },
      datePickerOptions: {
        disabledDate(time) {
          return time.getTime() < new Date('2026-05-01T00:00:00').getTime();
        },
        shortcuts: [
          {
            text: '今天',
            onClick(picker) {
              picker.$emit('pick', new Date('2026-06-02T00:00:00'));
            }
          }
        ]
      },
      dateRangePickerOptions: {
        disabledDate(time) {
          return time.getTime() < new Date('2026-05-01T00:00:00').getTime();
        },
        shortcuts: [
          {
            text: '未来 7 天',
            onClick(picker) {
              picker.$emit('pick', [
                new Date('2026-06-02T00:00:00'),
                new Date('2026-06-08T00:00:00')
              ]);
            }
          }
        ]
      },
      statusOptions: [
        { label: '启用', value: 'enabled' },
        { label: '草稿', value: 'draft' },
        { label: '停用', value: 'disabled' }
      ],
      regionOptions: [
        {
          value: 'brazil',
          label: 'Brazil',
          children: [
            {
              value: 'sp',
              label: 'Sao Paulo',
              children: [
                { value: 'campinas', label: 'Campinas' },
                { value: 'guarulhos', label: 'Guarulhos' }
              ]
            },
            {
              value: 'rj',
              label: 'Rio de Janeiro',
              children: [
                { value: 'rio-city', label: 'Rio City' }
              ]
            }
          ]
        }
      ],
      selectV2Options: [
        { value: 'site-01', label: 'SP-Guarulhos Hub' },
        { value: 'site-02', label: 'SP-Campinas Hub' },
        { value: 'site-03', label: 'RJ-Capital Hub' }
      ],
      categoryFilters: [
        { text: '基础', value: '基础' },
        { text: '表单', value: '表单' },
        { text: '反馈', value: '反馈' },
        { text: '边界', value: '边界' }
      ],
      tableData: [
        {
          name: 'el-button',
          category: '基础',
          count: 12,
          status: '已覆盖',
          statusType: 'success',
          arcoPath: 'button/style/index.less',
          desc: '包含主按钮、朴素按钮、圆角按钮和按钮组。'
        },
        {
          name: 'el-form',
          category: '表单',
          count: 18,
          status: '已覆盖',
          statusType: 'success',
          arcoPath: 'form/input/select/date-picker style',
          desc: '包含 input、number、select、cascader、picker、upload。'
        },
        {
          name: 'el-dialog',
          category: '反馈',
          count: 8,
          status: '可交互',
          statusType: 'warning',
          arcoPath: 'modal/drawer/popover style',
          desc: '通过本地状态控制开关，便于查看弹层效果。'
        },
        {
          name: '第三方组件',
          category: '边界',
          count: 3,
          status: '占位',
          statusType: 'info',
          arcoPath: '按容器继承 Element 主题',
          desc: 'virtual-list、apexchart、vue-esign 使用静态占位卡片。'
        }
      ],
      treeData: [
        {
          id: 1,
          label: '验收范围',
          children: [
            {
              id: 11,
              label: '基础组件'
            },
            {
              id: 12,
              label: '表单组件'
            },
            {
              id: 13,
              label: '数据与反馈组件'
            }
          ]
        }
      ],
      timelineData: [
        {
          time: '09:00',
          title: '加载全局主题资源',
          type: 'primary'
        },
        {
          time: '09:10',
          title: '检查按钮、表单、弹层主色',
          type: 'success'
        },
        {
          time: '09:20',
          title: '记录视觉偏差与未覆盖项',
          type: 'warning'
        }
      ],
      carouselItems: [
        {
          title: '主色检查',
          desc: '观察按钮、步骤条、标签和进度条是否使用统一品牌绿色。'
        },
        {
          title: '边框检查',
          desc: '观察输入框、卡片和表格边框是否存在风格漂移。'
        },
        {
          title: '层级检查',
          desc: '观察弹层、阴影、标题和正文的视觉层级是否清晰。'
        }
      ],
      scrollbarItems: [
        '按钮主色对比',
        '链接 hover 颜色',
        '输入框 focus 边框',
        'Select 下拉面板阴影',
        'DatePicker 面板标题',
        'Table 表头背景',
        'Pagination 当前页样式',
        'Dialog 遮罩层透明度',
        'Drawer 内边距',
        'Tabs 激活态',
        'Collapse 头部字重',
        'Carousel 指示器',
        'Scrollbar 滑块颜色',
        'Tooltip / Popover 气泡样式'
      ],
      horizontalItems: [
        '横向滚动项 01',
        '横向滚动项 02',
        '横向滚动项 03',
        '横向滚动项 04',
        '横向滚动项 05',
        '横向滚动项 06',
        '横向滚动项 07'
      ],
      shellMenu: [
        {
          label: '数据看板',
          icon: 'el-icon-data-analysis',
          active: true
        },
        {
          label: '订单中心',
          icon: 'el-icon-truck',
          active: false
        },
        {
          label: '结算管理',
          icon: 'el-icon-bank-card',
          active: false
        },
        {
          label: '系统设置',
          icon: 'el-icon-setting',
          active: false
        }
      ]
    };
  },
  methods: {
    filterCategory(value, row) {
      return row.category === value;
    },
    showMessage(type) {
      this.$message({
        message: `品牌 Message：${type} 状态用于检查圆角、图标、文字和状态色。`,
        type
      });
    },
    showNotification(type) {
      this.$notify({
        title: `Notification ${type} 示例`,
        message: '用于观察右上角通知的阴影、边框、图标与关闭按钮。',
        type
      });
    },
    showMessageBox() {
      this.$confirm('确认弹窗用于检查 MessageBox 的标题、正文、按钮和遮罩。', 'MessageBox 示例', {
        confirmButtonText: '确认',
        cancelButtonText: '取消',
        type: 'warning'
      }).catch(() => {});
    },
    showLoading() {
      const loading = this.$loading({
        lock: true,
        text: 'Loading 品牌遮罩演示',
        background: 'rgba(245, 248, 247, 0.72)'
      });
      window.setTimeout(() => {
        loading.close();
      }, 900);
    },
    handleUploadChange(file, fileList) {
      this.uploadFiles = fileList.slice(-3);
    },
    handleUploadRemove(file, fileList) {
      this.uploadFiles = fileList;
    }
  }
};
</script>

<style lang="scss" scoped>
.arco-brand-preview {
  min-height: 100%;
  padding: 24px;
  background:
    linear-gradient(180deg, rgba(0, 165, 81, 0.08), rgba(255, 255, 255, 0) 180px),
    #f5f7fa;
}

.hero {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 16px;
  padding: 24px;
  border-radius: 20px;
  background: #ffffff;
  box-shadow: 0 10px 30px rgba(31, 35, 41, 0.06);
}

.hero h1 {
  margin: 8px 0 10px;
  font-size: 28px;
  line-height: 1.2;
  color: #1f2329;
}

.hero__eyebrow {
  margin: 0;
  color: #00a551;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.hero__desc {
  max-width: 760px;
  margin: 0;
  color: #4e5969;
  line-height: 1.7;
}

.hero__meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
}

.hero__badge {
  line-height: 1;
}

.preview-alert {
  margin-bottom: 20px;
}

.preview-section {
  margin-bottom: 20px;
}

.section-title {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: baseline;
  gap: 8px;
  margin-bottom: 12px;
}

.section-title h2 {
  margin: 0;
  font-size: 20px;
  color: #1f2329;
}

.section-title span {
  color: #86909c;
  font-size: 13px;
}

.preview-card {
  margin-bottom: 16px;
  border-radius: 18px;
}

.inline-wrap {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 16px;
}

.media-row {
  align-items: center;
}

.preview-image {
  width: 96px;
  height: 64px;
  border: 1px solid #e8eeeb;
  box-shadow: 0 10px 24px rgba(31, 35, 41, 0.06);
}

.divider-demo {
  color: #4e5969;
}

.muted-text {
  margin: 0;
  color: #86909c;
  line-height: 1.7;
}

.preview-form {
  margin-top: 8px;
}

.pagination-wrap {
  display: flex;
  justify-content: flex-end;
  margin-top: 16px;
}

.pagination-wrap--compact {
  justify-content: space-between;
  gap: 12px;
}

.table-expand {
  padding: 14px 18px;
  color: #4e5969;
  line-height: 1.7;
}

.stack-wrap {
  display: grid;
  gap: 16px;
}

.preview-tabs {
  margin-top: 16px;
}

.preview-steps-simple,
.preview-steps-vertical {
  margin-top: 18px;
}

.preview-steps-vertical {
  min-height: 190px;
}

.carousel-slide {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 10px;
  height: 100%;
  border-radius: 14px;
  color: #ffffff;
  background: linear-gradient(135deg, #00a551, #37c87a);
  text-align: center;
}

.carousel-slide strong {
  font-size: 20px;
}

.carousel-slide span {
  max-width: 320px;
  line-height: 1.6;
}

.scroll-demo {
  padding-right: 12px;
}

.scroll-item {
  padding: 12px 14px;
  border-bottom: 1px solid #ebeef5;
  color: #4e5969;
}

.horizontal-scrollbar {
  margin-top: 14px;
  padding-bottom: 10px;
}

.horizontal-scrollbar__content {
  display: inline-flex;
  gap: 12px;
  min-width: 980px;
  padding: 8px 4px;
}

.horizontal-scrollbar__content span {
  min-width: 130px;
  padding: 10px 12px;
  border: 1px solid #e8eeeb;
  border-radius: 999px;
  color: #4e5969;
  background: #fff;
  text-align: center;
}

.preview-menu {
  max-width: 360px;
  border: 1px solid #e8eeeb;
  border-radius: 16px;
  padding: 8px;
}

.container-demo {
  min-height: 180px;
  border: 1px solid #e8eeeb;
  border-radius: 18px;
  overflow: hidden;
  background: #fff;
}

.container-demo .el-aside {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #00a551;
  background: #e8f8ef;
  font-weight: 600;
}

.container-demo .el-main {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  color: #4e5969;
  background: linear-gradient(135deg, #ffffff, #f5fbf7);
}

.container-demo__icon {
  color: #00a551;
  font-size: 24px;
}

.shell-card {
  overflow: hidden;
}

.shell-demo {
  display: grid;
  grid-template-columns: 220px minmax(0, 1fr);
  min-height: 430px;
  border: 1px solid #e5e6eb;
  border-radius: 18px;
  overflow: hidden;
  background:
    radial-gradient(circle at 100% 0, rgba(0, 165, 81, 0.08), transparent 34%),
    #f7f8fa;
}

.shell-sidebar {
  padding: 10px 8px 18px;
  background:
    linear-gradient(180deg, rgba(0, 165, 81, 0.035), rgba(255, 255, 255, 0) 32%),
    #ffffff;
  border-right: 1px solid #e5e6eb;
  box-shadow: 10px 0 30px rgba(29, 33, 41, 0.04);
}

.shell-logo {
  display: flex;
  align-items: center;
  gap: 10px;
  height: 58px;
  padding: 0 10px;
  margin-bottom: 10px;
  border-bottom: 1px solid rgba(229, 230, 235, 0.72);
  color: #1f2329;
  font-weight: 700;
}

.shell-logo__mark {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: 14px;
  color: #ffffff;
  background: linear-gradient(135deg, #00a551, #37c87a);
  box-shadow: 0 10px 22px rgba(0, 165, 81, 0.24);
}

.shell-menu-item {
  position: relative;
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 42px;
  padding: 0 14px;
  margin-bottom: 4px;
  border-radius: 10px;
  color: #4e5969;
  font-weight: 500;
  transition: all 0.2s ease;
}

.shell-menu-item i {
  font-size: 17px;
}

.shell-menu-item.is-active {
  color: #00a551;
  background: linear-gradient(90deg, rgba(0, 165, 81, 0.15), rgba(0, 165, 81, 0.07));
  box-shadow:
    inset 0 0 0 1px rgba(0, 165, 81, 0.1),
    0 8px 18px rgba(0, 165, 81, 0.08);
}

.shell-menu-item.is-active::before {
  content: '';
  position: absolute;
  left: 8px;
  top: 50%;
  width: 3px;
  height: 16px;
  border-radius: 999px;
  background: #00a551;
  transform: translateY(-50%);
}

.shell-main {
  min-width: 0;
}

.shell-navbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  height: 60px;
  padding: 0 20px;
  background: rgba(255, 255, 255, 0.96);
  border-bottom: 1px solid #e5e6eb;
  box-shadow: 0 8px 24px rgba(29, 33, 41, 0.045);
  backdrop-filter: saturate(140%) blur(10px);
}

.shell-breadcrumb,
.shell-actions {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}

.shell-breadcrumb {
  color: #4e5969;
}

.shell-breadcrumb span {
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.shell-actions > span {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: 1px solid #f0f1f5;
  border-radius: 50%;
  color: #4e5969;
  background: #ffffff;
}

.shell-user {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  min-width: 176px;
  padding: 5px 12px 5px 7px;
  border: 1px solid #e5e6eb;
  border-radius: 999px;
  background: #ffffff;
  box-shadow: 0 10px 24px rgba(31, 35, 41, 0.06);
}

.shell-user strong,
.shell-user em {
  display: block;
  line-height: 1.2;
}

.shell-user strong {
  color: #1f2329;
  font-size: 13px;
}

.shell-user em {
  color: #86909c;
  font-size: 12px;
  font-style: normal;
}

.shell-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  min-height: 40px;
  padding: 6px 20px;
  background: rgba(255, 255, 255, 0.94);
  border-bottom: 1px solid #e5e6eb;
  box-shadow: 0 6px 18px rgba(29, 33, 41, 0.035);
}

.shell-tag {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 28px;
  padding: 0 10px;
  border: 1px solid #e5e6eb;
  border-radius: 8px;
  color: #4e5969;
  background: #f7f8fa;
}

.shell-tag.is-active {
  color: #ffffff;
  background: linear-gradient(135deg, #00a551, #12b866);
  border-color: #00a551;
  box-shadow: 0 8px 18px rgba(0, 165, 81, 0.18);
}

.shell-content {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px;
  padding: 16px;
}

.shell-stat {
  min-height: 132px;
  padding: 18px;
  border: 1px solid #e5e6eb;
  border-radius: 20px;
  background: linear-gradient(135deg, #ffffff, #f5fbf7);
  box-shadow: 0 10px 30px rgba(29, 33, 41, 0.055);
}

.shell-stat span {
  color: #86909c;
}

.shell-stat strong {
  display: block;
  margin-top: 18px;
  color: #1f2329;
  font-size: 28px;
}

.placeholder-card p {
  margin: 0 0 10px;
  color: #4e5969;
  line-height: 1.7;
}

.drawer-content {
  padding: 0 20px 20px;
}

@media (max-width: 768px) {
  .arco-brand-preview {
    padding: 16px;
  }

  .hero {
    flex-direction: column;
    padding: 18px;
    border-radius: 16px;
  }

  .hero h1 {
    font-size: 24px;
  }

  .hero__meta {
    justify-content: flex-start;
  }

  .shell-demo {
    grid-template-columns: 1fr;
  }

  .shell-sidebar {
    border-right: 0;
    border-bottom: 1px solid #e8eeeb;
  }

  .shell-navbar {
    height: auto;
    align-items: flex-start;
    flex-direction: column;
    padding: 14px;
  }

  .shell-actions {
    width: 100%;
    flex-wrap: wrap;
  }

  .shell-user {
    min-width: 0;
    flex: 1;
  }

  .shell-content {
    grid-template-columns: 1fr;
    padding: 14px;
  }
}
</style>
